import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LoadingPage } from '@/components/common';
import { useOnboarding } from '@/hooks/useChild';

interface RequireNoChildProps {
  children: React.ReactNode;
}

/**
 * RequireNoChild - Reverse guard component (for Onboarding page)
 *
 * Ensures user doesn't have a child profile yet:
 * - Shows Loading while checking
 * - Redirects to home if child profile exists
 * - Renders children (shows Onboarding) if no child profile
 *
 * Note: Should be used after AuthGuard to ensure user is logged in
 */
export function RequireNoChild({ children }: RequireNoChildProps) {
  const { t } = useTranslation('auth');
  const { needsOnboarding, isLoading } = useOnboarding();

  // Still loading child profile status
  if (isLoading) {
    return <LoadingPage message={t('loading.loadingUser')} />;
  }

  // Has child profile, redirect to home
  if (!needsOnboarding) {
    return <Navigate to="/" replace />;
  }

  // No child profile, render Onboarding page
  return <>{children}</>;
}
